import type { DownloadDestination, BuiltinMediaDownloadDestination, ProjectFileDownloadDestination } from '../model/downloadDestination';
import type { OngoingDownload } from '../model/ongoingDownload';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { MediaDestination } from '@mimir/common/kelda/mediaDestination';
import type { Logger } from '@mimir/common/types/logger';
import { isFileAvailableLocally } from '@mimir/common/util/fs';
import { invariant } from '@mimir/common/util/invariant';


/**
 * Given an OngoingDownload object, this function checks the possible
 * destination path(s) and returns the first path that corresponds
 * to an existing file or undefined if no existing file has been found.
 */
export async function tryGetLocallyAvailableFile(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  ongoingDownload: OngoingDownload,
  localPathToCheck: string | undefined,
): Promise<string | undefined> {
  invariant(ongoingDownload.downloadDestination, 'Cannot invoke tryGetLocallyAvailableFile without a download destination');
  const possiblePaths: string[] = [];
  if (localPathToCheck) {
    possiblePaths.push(localPathToCheck);
  }
  possiblePaths.push(...getPossibleDestinationPaths(ongoingDownload.downloadDestination));
  for (const possiblePath of possiblePaths) {
    if (await isFileAvailableLocally(services.applicationContext.nodeApi, services.logger, possiblePath)) {
      return possiblePath;
    }
  }

  return undefined;
}


/**
 * Given a download destination object, returns an array containing
 * the possible paths where the file will be once the download is complete.
 */
export function getPossibleDestinationPaths(downloadDestination: DownloadDestination | undefined): string[] {
  if (downloadDestination === undefined) {
    return [];
  }
  switch (downloadDestination.type) {
    case 'builtin-media-download':
    case 'external-media-download': return [downloadDestination.legacyDestinationPath, downloadDestination.subfoldersDestinationPath];
    case 'builtin-project-file-download': return [downloadDestination.projectFilePath];
    case 'builtin-attachment-file-download': return [downloadDestination.attachmentFilePath];
  }
}


/**
 * Given a download destination for the built in storage manager,
 * this function returns the local path to actually download the file to.
 */
export function getBuiltinDownloadDestinationPath(downloadDestination: BuiltinMediaDownloadDestination | ProjectFileDownloadDestination) {
  if (downloadDestination.type === 'builtin-project-file-download') {
    return downloadDestination.projectFilePath;
  }

  return downloadDestination.builtinDownloadDestination === 'legacy'
    ? downloadDestination.legacyDestinationPath
    : downloadDestination.subfoldersDestinationPath;
}


/**
 * Given a MediaDestination object that describes both a legacy path and a
 * path using subfolders, this function returns a MediaDownloadDestination
 * object that has 2 purposes:
 * - if the download it to be done by a builtin storage manager,
 *   "builtinDownloadDestination" will indicate which of the 2 paths
 *   should be used as the target path to download to
 * - it embeds the 2 possible paths so that, after the download
 *   operation is complete, we can check these 2 locations.
 *   Note that we will always check the 2 locations even when using
 *   the built storage manager, because we want to be able to use subfolders
 *   paths by just moving files around on disk without updating any item property
 */
export function getDownloadDestination(
  isPremiereProjectFile: boolean,
  mediaDestination: MediaDestination,
  useBuiltinStorageManager: boolean,
  downloadToSubfolderPaths: boolean,
): DownloadDestination {
  if (isPremiereProjectFile) {
    return {
      type: 'builtin-project-file-download',
      projectFilePath: mediaDestination.legacyDestinationPath,
    };
  }

  if (useBuiltinStorageManager) {
    return {
      type: 'builtin-media-download',
      ...mediaDestination,
      builtinDownloadDestination: downloadToSubfolderPaths ? 'subfolders' : 'legacy',
    };
  }

  return {
    ...mediaDestination,
    type: 'external-media-download',
  };
}
